<template>
  <v-app-bar
    class="pos-header"
    :class="{
      'pos-header--landing-top': isLanding && isAtTop,
      'pos-header--landing-scrolled': isLanding && !isAtTop
    }"
    light
    elevation="0"
    elevate-on-scroll
    color="transparent"
    fixed
    app
  >
    <div class="container-xl pos-header__inner">
      <v-app-bar-nav-icon
        v-if="$vuetify.breakpoint.smAndDown"
        @click="$emit('drawer')"
        aria-label="Open navigation"
      />
      <div class="pos-header__left">
        <v-btn to="/" link text class="brand-btn">
          <div class="d-flex align-center">
            <app-logo
              :size="$vuetify.breakpoint.mdAndUp ? 38 : 34"
              :with-text="!$vuetify.breakpoint.smAndDown"
              :text="title"
              variant="dark"
            />
          </div>
        </v-btn>
      </div>

      <template v-if="!$vuetify.breakpoint.smAndDown">
        <div class="pos-header__center">
          <template v-if="isLanding">
            <v-btn
              v-for="link in landingLinks"
              :key="link.id"
              text
              class="font-weight-medium text-capitalize nav-btn"
              @click.prevent="scrollTo(link.id)"
              :aria-label="link.ariaLabel"
            >
              {{ $t(link.text) || link.fallback }}
            </v-btn>
          </template>

          <template v-else>
            <template v-for="(item, index) in items">
              <v-menu v-if="item.items.length" :key="`menu-${index}`" offset-y bottom left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    text
                    class="font-weight-medium text-capitalize nav-btn"
                  >
                    {{ $t(item.text) }}
                    <v-icon small right> fal fa-angle-down </v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item
                    dense
                    v-for="(subItem, i) in item.items"
                    :key="i"
                    :to="subItem.to"
                    router
                    exact
                  >
                    <v-list-item-title>{{ $t(subItem.text) }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <v-btn
                v-else
                :to="item.to"
                text
                :key="`link-${index}`"
                class="font-weight-medium rounded-lg text-capitalize nav-btn"
              >
                {{ $t(item.text) }}
              </v-btn>
            </template>
          </template>
        </div>

        <div class="pos-header__right">
          <v-btn
            text
            to="/login"
            class="font-weight-medium rounded-lg text-capitalize nav-btn"
            aria-label="Login"
          >
            {{ $t("login") || "Login" }}
          </v-btn>
          <v-btn depressed to="/register" class="font-weight-medium rounded-lg text-capitalize cta-btn">
            <v-icon left small>fal fa-rocket</v-icon>
            {{ $t("register") }}
          </v-btn>
          <v-menu offset-y bottom right>
            <template v-slot:activator="{ attrs, on }">
              <v-btn icon v-bind="attrs" v-on="on" class="font-weight-medium lang-btn" aria-label="Change language">
                <v-icon>fal fa-globe</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-for="(locale, index) in locales"
                :key="index"
                @click="changeLocale(locale.code)"
              >
                <v-list-item-avatar size="20">
                  <v-img :src="locale.flag" />
                </v-list-item-avatar>

                <v-list-item-title>{{ locale.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>
    </div>
  </v-app-bar>
</template>

<script>
import { mapGetters } from "vuex";
import AppLogo from "@/components/global/AppLogo.vue";
export default {
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: "PosSmart",
    },
    img: {
      type: String,
      default: "/images/logo_white.png",
    },
  },
  components: {
    AppLogo,
  },
  computed: {
    ...mapGetters({
      locales: "auth/locales",
    }),
    isLanding() {
      return this.$route?.path === "/";
    },
    landingLinks() {
      return [
        {
          id: "features",
          text: "features",
          fallback: "Features",
          ariaLabel: "Jump to features",
        },
        {
          id: "packages",
          text: "packages",
          fallback: "Packages",
          ariaLabel: "Jump to pricing",
        },
        {
          id: "faq",
          text: "faq",
          fallback: "FAQ",
          ariaLabel: "Jump to FAQ",
        },
      ];
    },
  },
  data() {
    return {
      isAtTop: true,
    };
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    this.handleScroll();
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    changeLocale(code) {
      localStorage.setItem("locale", code);
      this.$i18n.locale = code;
    },
    handleScroll() {
      this.isAtTop = (window.scrollY || 0) < 8;
    },
    scrollTo(id) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
  },
};
</script>

<style scoped>
@import "@/styles/landing-light.css";

.pos-header {
  border-bottom: 1px solid var(--glass-border);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  background: rgba(255, 255, 255, 0.78) !important;
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
}

.pos-header__inner {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0 16px;
}

@media (min-width: 960px) {
  .pos-header__inner {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 24px;
  }
}

.pos-header__left {
  display: flex;
  align-items: center;
  min-width: 180px;
}

.pos-header__center {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 0;
}

.pos-header__right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 180px;
}

/* Landing Top State - More Transparent */
.pos-header.pos-header--landing-top {
  background: rgba(255, 255, 255, 0.62) !important;
  border-bottom-color: rgba(15, 23, 42, 0.08) !important;
}

/* Landing Scrolled State - More Opaque */
.pos-header.pos-header--landing-scrolled {
  background: rgba(255, 255, 255, 0.92) !important;
  border-bottom-color: var(--glass-border) !important;
  box-shadow: 0 8px 26px rgba(15, 23, 42, 0.12);
}

.brand-btn {
  text-transform: none;
}

.brand-title {
  color: var(--text-primary);
  letter-spacing: -0.2px;
  font-weight: 700;
}

.nav-btn {
  color: var(--text-secondary);
  text-transform: none;
  border-radius: 10px;
  transition: all 200ms ease;
}

.nav-btn:hover {
  background-color: var(--bg-glass-hover);
  color: var(--text-primary);
}

.cta-btn {
  text-transform: none;
  background: var(--color-primary) !important;
  color: #ffffff !important;
  box-shadow: 0 10px 26px var(--color-primary-20);
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
}

.cta-btn ::v-deep .v-icon {
  color: #ffffff !important;
}

.cta-btn:hover {
  background: var(--color-primary-hover) !important;
  box-shadow: 0 14px 34px var(--color-primary-20);
  transform: translateY(-2px);
}

.cta-btn:focus-visible {
  outline: 3px solid var(--color-primary-20);
  outline-offset: 3px;
}

.lang-btn {
  color: var(--text-secondary);
}

.lang-btn:hover {
  color: var(--text-primary);
}

/* Dropdown Styles */
::v-deep .v-list {
  background: var(--bg-primary);
  border: 1px solid var(--glass-border);
  border-radius: 12px;
}

::v-deep .v-list-item {
  color: var(--text-secondary);
}

::v-deep .v-list-item:hover::before {
  background-color: var(--bg-glass);
}

::v-deep .v-list-item--active {
  color: var(--text-primary);
  background-color: var(--bg-glass-hover);
}

/* Menu Styles */
::v-deep .v-menu__content {
  background: var(--bg-primary);
  border: 1px solid var(--glass-border);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

::v-deep .v-menu__content .v-list-item {
  color: var(--text-secondary);
}

::v-deep .v-menu__content .v-list-item:hover {
  background-color: var(--bg-glass-hover);
  color: var(--text-primary);
}
</style>
